<template>
  <div class="commerceConnector">
    <div class="commerceConnector__layout">
      <button
        class="commerceConnector__close"
        :data-tab-close="$options.settings.COMMERCE_CONNECTOR_TAB_ID"
        data-tab-a11y-focus
      >
        <svg class="icon" role="img" aria-labelledby="closeCommerceConnector" aria-hidden="true">
          <title id="closeCommerceConnector">Close window</title>
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#close">
            {{ closeIconText }}
          </use>
        </svg>
      </button>
      <form>
        <div class="commerceConnector__wrapper">
          <tabs v-if="formattedData && formattedData.length > 0" :key="formattedData.length">
            <tab
              v-for="(country, index) in formattedData"
              :key="`tab-${index}`"
              :name="country.countryCode || country.CountryCode || country.Country || 'default'"
            >
              <fieldset v-if="shouldShowFieldset(country)">
                <legend
                  class="commerceConnector__description"
                  :class="{
                    '-shopsNotAvailable': country.shopsUnavailable || country.ShopsUnavailable,
                  }"
                >
                  <template v-if="country.shopsUnavailable || country.ShopsUnavailable">
                    {{ country.defaultDescriptionTitle || country.DefaultDescriptionTitle }}
                  </template>
                  <template v-else>
                    <strong class="commerceConnector__description-title">
                      {{ country.descriptionProduct || country.DescriptionProduct }}
                    </strong>
                    <span class="commerceConnector__description-text">
                      {{ country.descriptionText || country.DescriptionText }}
                    </span>
                  </template>
                </legend>
              </fieldset>
              <ul
                v-if="!(country.shopsUnavailable || country.ShopsUnavailable)"
                class="commerceConnector__shops"
              >
                <li
                  v-for="shopData in (country.shops || country.Shops || []).filter((e) => {
                    return qrCodeRetailer
                      ? qrCodeRetailer ==
                          (e.title || e.Title || '').toLowerCase().replace(/\s+/, '-')
                      : e;
                  })"
                  :key="shopData.id || shopData.Id"
                  class="commerceConnector__item"
                  :class="{
                    '-unavailable': shopData.unavailable || shopData.Unavailable,
                    handleBuy__eRetailer: addClassToHandleBuyTagging,
                  }"
                >
                  <a
                    :href="shopData.link || shopData.Link"
                    class="commerceConnector__radioLabel commerceConnector__tabIndex commerceConnector__link"
                    :class="{
                      'commerceConnector__link--has-button': showShowBuyButton(
                        country.stockStatusVisibility || country.StockStatusVisibility,
                        shopData.stockStatus || shopData.StockStatus
                      ),
                    }"
                    target="_blank"
                    @click="getTaggingInfo(shopData)"
                  >
                    <img
                      v-if="shopData.logo || shopData.Logo"
                      class="commerceConnector__logo"
                      :src="shopData.logo || shopData.Logo"
                      alt=""
                    />

                    <button
                      v-if="
                        showShowBuyButton(
                          country.stockStatusVisibility || country.StockStatusVisibility,
                          shopData.stockStatus || shopData.StockStatus
                        )
                      "
                      class="oap-button -primary"
                      :disabled="shopData.unavailable || shopData.Unavailable"
                      type="button"
                    >
                      <span class="oap-button__label">
                        {{ shopData.stockStatus || shopData.StockStatus }}
                      </span>
                    </button>
                    <span v-else>
                      {{ shopData.title || shopData.Title }}
                    </span>
                  </a>
                </li>
              </ul>
            </tab>
          </tabs>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { AnalyticsHandler } from '../../../../../../Foundation/Core/code/Scripts';
import eventBus from '@refapp/eventbus-js';
import { mixinParseURL } from '../../../../../../Foundation/Core/code/Scripts/mixins/parseURL/parseURL';

export default {
  name: 'CommerceConnector',
  api: {
    events: {
      ready: 'ready',
      toggle: 'toggle',
    },
  },
  mixins: [mixinParseURL],
  props: {
    initialData: {
      type: [Array, Object, String],
      required: false,
      default: () => [],
      validator: (value) => {
        // Accept empty string, null, undefined, arrays, or objects
        return (
          value === '' ||
          value === null ||
          value === undefined ||
          Array.isArray(value) ||
          typeof value === 'object'
        );
      },
    },
    closeIconText: { type: String, required: false, default: 'Close window' },
    productId: {
      type: String,
      required: false,
      default: null,
    },
  },
  settings: {
    COMMERCE_CONNECTOR_TAB_ID: 'commerce-connector-tab',
    COMMERCE_CONNECTOR_URL_PARAM: 'buy',
    VTO_REDIRECT_URL_PARAM: 'vto-redirect',
  },
  data() {
    return {
      ccConnectorEnabled: false,
      addClassToHandleBuyTagging: false,
      qrCodeRetailer: false,
      filterData: '',
      commerceData: null,
    };
  },
  computed: {
    formattedData() {
      const data = this.commerceData || this.initialData;

      // Handle null/undefined/empty string data
      if (!data || data === '' || (Array.isArray(data) && data.length === 0)) {
        return [];
      }

      if (Array.isArray(data)) {
        if (this.ccConnectorEnabled && data.length > 0) {
          return [data[0]];
        }
        return data;
      }

      // If data is a single object (not array), wrap it in array
      if (typeof data === 'object' && data !== null) {
        return [data];
      }

      return [data];
    },
    hasData() {
      return (
        this.formattedData &&
        Array.isArray(this.formattedData) &&
        this.formattedData.length > 0 &&
        this.formattedData.some((country) => {
          if (!country) {
            return false;
          }

          // Check both lowercase 'shops' and uppercase 'Shops' (API inconsistency)
          const shops = country.shops || country.Shops;

          return shops && Array.isArray(shops) && shops.length > 0;
        })
      );
    },
  },
  created() {
    /* istanbul ignore next */
    if (this.URLHasKey(this.$options.settings.COMMERCE_CONNECTOR_URL_PARAM)) {
      setTimeout(() => {
        eventBus.emit('tab:open:external', this.$options.settings.COMMERCE_CONNECTOR_TAB_ID);
      }, 0);
    }

    /* istanbul ignore next */
    eventBus.on(`commerce-connector:${this.$options.api.events.toggle}`, (open) => {
      setTimeout(() => {
        if (open) {
          eventBus.emit('tab:open:external', this.$options.settings.COMMERCE_CONNECTOR_TAB_ID);
        }
      }, 0);
    });
  },
  mounted() {
    let urlSearchParams = new URLSearchParams(window.location.search);
    let params = Object.fromEntries(urlSearchParams.entries());
    let paramsValue = params.utm_retailer;
    if (paramsValue) {
      this.qrCodeRetailer = paramsValue.toLowerCase().replace(/\s+/, '-');
    }
    this.ccConnectorEnabled =
      document.getElementById('ccConnectorEnabled')?.value.toLowerCase() === 'true';

    if (this.URLHasKey(this.$options.settings.VTO_REDIRECT_URL_PARAM)) {
      this.addClassToHandleBuyTagging = true;
    }

    // Fetch commerce connector data from API
    try {
      this.fetchCommerceData();
    } catch (error) {
      console.error('Error calling fetchCommerceData:', error);
    }
  },
  methods: {
    fetchCommerceData() {
      if (!this.productId) {
        return;
      }

      // Clean productId - remove curly braces if present
      const cleanProductId = this.productId.replace(/[{}]/g, '');

      const timestamp = new Date().getTime();
      const apiUrl = `/api/sitecore/CommerceConnectorApi/CommerceConnector?itemId=${cleanProductId}&_t=${timestamp}`;

      fetch(apiUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          this.commerceData = data;

          // Force Vue to update the component
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        })
        .catch((error) => {
          console.error('❌ Error fetching commerce connector data:', error);
        });
    },
    showShowBuyButton(stockStatusVisibility, stockStatus) {
      // Handle both camelCase and PascalCase from API
      const visibility = stockStatusVisibility || false;
      const status = stockStatus || false;
      return visibility && status;
    },
    shouldShowFieldset(country) {
      return (
        country.defaultDescriptionTitle ||
        country.DefaultDescriptionTitle ||
        country.descriptionProduct ||
        country.DescriptionProduct ||
        country.descriptionText ||
        country.DescriptionText
      );
    },
    getTaggingInfo(shopData) {
      if (!shopData) {
        return;
      }

      // Clone shopData FIRST, before accessing nested taggingInfo
      // This prevents Vue reactivity from corrupting the nested ecommerce object
      const cleanShopData = JSON.parse(JSON.stringify(shopData));
      const taggingData = cleanShopData.taggingInfo || cleanShopData.TaggingInfo;

      if (!taggingData) {
        return;
      }

      // Access properties directly from the clean taggingData
      const eventAction = taggingData.eventAction;
      const eventLabel = taggingData.eventLabel;
      const ecommerce = taggingData.ecommerce;
      const eventName = taggingData.eventName;
      const step = taggingData.step;
      const productInfo = taggingData.productInfo;
      const storeName = taggingData.storeName;
      const price = taggingData.price;

      const handBuyactive = document.querySelector('.commerceConnector__item');
      const handBuyClick = handBuyactive?.classList.contains('handleBuy__eRetailer');
      const handBuypageURL = new URLSearchParams(window.location.search);
      const handBuylastURL = handBuypageURL.has('utm_medium');
      if ((handBuyClick === true || handBuylastURL === true) && ecommerce?.add?.products?.[0]) {
        ecommerce.add.products[0].dimension49 = 'activated';
      }
      /* istanbul ignore next */
      AnalyticsHandler.getAnalyticsHandler().push({
        type: 'addToCart',
        ecommerce,
        category: 'Ecommerce',
        action: eventAction,
        label: eventLabel,
        event_name: eventName,
        step,
        productInfo,
        storeName,
        price,
      });
    },
  },
};
</script>
